import { useEffect, useRef, useState } from "react";
import "./management.css";
import EAIMLogo from "../img/eaim_logo_w.png";

const HARD_PIN = "608521";
const API_BASE = process.env.REACT_APP_API || "";

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

function PinModal({ open, onVerified }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      setErr("");
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
  }, [open]);

  const handleChange = (idx, value) => {
    const v = value.replace(/\D/g, "").slice(0, 1);
    if (!/^\d?$/.test(v)) return;
    setErr("");
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0)
      inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputsRef.current[idx + 1]?.focus();
    if (e.key === "Enter") submit();
  };

  const submit = () => {
    const pin = digits.join("");
    if (pin.length !== 6) {
      setErr("Please enter all 6 digits.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      if (pin !== HARD_PIN) {
        setErr("Invalid PIN");
        setLoading(false);
        setDigits(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
        return;
      }
      onVerified();
      setLoading(false);
    }, 200);
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="pin-modal">
        <div className="pin-head">
          <div className="pin-title">Enter 6-digit passcode</div>
        </div>
        <div className="pin-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              type="password"
              inputMode="numeric"
              maxLength={1}
              className="pin-box"
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              autoComplete="one-time-code"
            />
          ))}
        </div>
        {err && <div className="pin-error">{err}</div>}
        <div className="pin-actions">
          <button
            className="btn"
            onClick={submit}
            disabled={loading}
            style={{ marginTop: "15px" }}
          >
            {loading ? "Checking…" : "Unlock"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Management() {
  const [verified, setVerified] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [refreshRate, setRefreshRate] = useState("15");
  const [dept] = useState("notice");

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [listLoading, setListLoading] = useState(false);
  const [annList, setAnnList] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (verified) fetchUpcoming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified]);

  const fetchUpcoming = async () => {
    setListLoading(true);
    try {
      const res = await fetch(`${API_BASE}/listUpcomingAnnouncements`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const ct = res.headers.get("content-type") || "";
      const text = await res.text();
      const json = ct.includes("application/json") ? JSON.parse(text) : null;
      if (!res.ok || !json?.success)
        throw new Error(json?.message || text || `HTTP ${res.status}`);
      setAnnList(json.data || []);
    } catch (e) {
      setAnnList([]);
      console.error("fetchUpcoming error:", e.message);
    } finally {
      setListLoading(false);
    }
  };

  const resetForm = () => {
    setFromDate("");
    setToDate("");
    setAnnouncement("");
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFromDate(item.fromDate ? fmtDate(item.fromDate) : "");
    setToDate(item.toDate ? fmtDate(item.toDate) : "");
    setAnnouncement(item.announcement || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const res = await fetch(`${API_BASE}/deleteAnnouncement/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      });
      const ct = res.headers.get("content-type") || "";
      const text = await res.text();
      const json = ct.includes("application/json") ? JSON.parse(text) : null;
      if (!res.ok || !json?.success)
        throw new Error(json?.message || text || `HTTP ${res.status}`);
      fetchUpcoming();
      if (editingId === id) resetForm();
    } catch (e) {
      alert(e.message || "Failed to delete.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveMsg("");
    setSaving(true);

    try {
      if (editingId) {
        const payload = {
          fromDate: fromDate || null,
          toDate: toDate || null,
          dept,
          announcement: (announcement || "").trim(),
        };
        const res = await fetch(`${API_BASE}/updateAnnouncement/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        const ct = res.headers.get("content-type") || "";
        const text = await res.text();
        const json = ct.includes("application/json") ? JSON.parse(text) : null;
        if (!res.ok || !json?.success)
          throw new Error(json?.message || text || `HTTP ${res.status}`);
        setSaveMsg("Announcement updated.");
        await fetchUpcoming();
        setEditingId(null);
      } else {
        const payload = {
          fromDate: fromDate || null,
          toDate: toDate || null,
          announcement: (announcement || "").trim(),
          refreshRate: refreshRate ? Number(refreshRate) : null,
          dept,
        };
        const res = await fetch(`${API_BASE}/saveManagementSettings`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        const ct = res.headers.get("content-type") || "";
        const text = await res.text();
        const json = ct.includes("application/json") ? JSON.parse(text) : null;
        if (!res.ok || (json && json.success === false)) {
          throw new Error(
            (json && json.message) || text || `HTTP ${res.status}`
          );
        }
        setSaveMsg(json?.message || "Saved successfully.");
        await fetchUpcoming();
      }
    } catch (err) {
      setSaveMsg(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const MAX_ANN_LEN = 2000;
  const used = announcement?.length ?? 0;
  const left = Math.max(0, MAX_ANN_LEN - used);

  return (
    <div className="mgmt-wrap">
      <PinModal open={!verified} onVerified={() => setVerified(true)} />

      <header className="mgmt-header" aria-hidden={!verified}>
        <div className="brand-row">
          <div className="brand-fallback">
            <img
              src={EAIMLogo}
              alt="EAIM Logo"
              style={{ height: 80, objectFit: "contain" }}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              background: "#B31E8D",
              fontSize: 30,
              padding: "12px 28px",
              color: "#fff",
              borderRadius: "40px",
              position: "absolute",
              bottom: -36,
              boxShadow: `
                0 8px 12px rgba(0,0,0,0.3),
                0 4px 6px rgba(0,0,0,0.2),
                inset 0 4px 6px rgba(255,255,255,0.3),
                inset 0 -4px 6px rgba(0,0,0,0.25)
              `,
            }}
          >
            Management
          </div>
        </div>
      </header>

      {verified && (
        <main className="mgmt-main">
          <form className="mgmt-form" onSubmit={handleSubmit}>
            <div className="section-title" style={{ marginBottom: "20px" }}>
              Create Announcement
            </div>
            <div className="grid-4">
              <div className="field">
                <label htmlFor="fromDate">From Date</label>
                <input
                  id="fromDate"
                  type="date"
                  className="input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="From date"
                />
              </div>

              <div className="field">
                <label htmlFor="toDate">To Date</label>
                <input
                  id="toDate"
                  type="date"
                  className="input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="To date"
                />
              </div>

              <div className="field ann-field">
                <label htmlFor="announcement">Announcement</label>
                <textarea
                  id="announcement"
                  className="input textarea"
                  rows={4}
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value)}
                  placeholder="Announcement"
                  maxLength={MAX_ANN_LEN}
                />
                <div className="ann-helper">
                  <span
                    className={"char-counter" + (left === 0 ? " at-limit" : "")}
                  >
                    {used}/{MAX_ANN_LEN}
                  </span>
                </div>
              </div>
            </div>

            <div className="section">
              <div className="section-title">Preferences</div>
              <div className="inline-field">
                <label htmlFor="refreshRate">Refresh Rate</label>
                <input
                  id="refreshRate"
                  type="number"
                  min="1"
                  step="1"
                  className="input input-sm"
                  value={refreshRate}
                  onChange={(e) => setRefreshRate(e.target.value)}
                />
                <span className="unit">sec</span>
              </div>

              <button
                className="btn"
                type="submit"
                disabled={saving}
                style={{ background: "#B31E8D", color: "#fff", marginTop: 20 }}
              >
                {saving
                  ? editingId
                    ? "Updating…"
                    : "Saving…"
                  : editingId
                  ? "Update"
                  : "Submit"}
              </button>

              {saveMsg && (
                <div style={{ marginTop: 8, fontSize: 14 }}>{saveMsg}</div>
              )}
            </div>
          </form>

          {/* Upcoming announcements */}
          <hr className="my-4" style={{ borderColor: "#ccc" }} />
          <section className="section" style={{ marginTop: 24 }}>
            <div className="section-title">Upcoming Announcements</div>
            {listLoading && <div>Loading…</div>}
            {!listLoading && annList.length === 0 && (
              <div style={{fontSize: "14px", marginTop: "20px"}}>No upcoming announcements.</div>
            )}

            <div className="cards-grid">
              {annList.map((item) => (
                <article key={item.id} className="card-announce">
                  <div className="card-dates">
                    {fmtDate(item.fromDate)} - {fmtDate(item.toDate)}
                  </div>
                  <div className="card-text">{item.announcement}</div>

                  <div className="card-actions">
                    <button
                      className="icon-btn"
                      title="Edit"
                      onClick={() => handleEdit(item)}
                      aria-label="Edit"
                      style={{ color: "blue" }}
                    >
                      {/* Pencil icon */}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.2-.2.2-.51 0-.71L15.5 5.29c-.2-.2-.51-.2-.71 0l-1.83 1.83 3.75 3.75 1.8-1.66z" />
                      </svg>
                    </button>
                    <button
                      className="icon-btn"
                      title="Delete"
                      onClick={() => handleDelete(item.id)}
                      aria-label="Delete"
                      style={{ color: "red" }}
                    >
                      {/* Trash icon */}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M6 7h12v2H6V7zm2 3h8v9H8V10zm3-7h2v2h-2V3z" />
                      </svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      )}
    </div>
  );
}
